/**
 * Converts an image file to a cropped PNG blob using OffscreenCanvas
 * Based on the CROPING_HOWTO.md implementation
 */

export interface CropArea {
    x: number
    y: number
    width: number
    height: number
}

/**
 * Crop the given image File to the rectangle described by `crop` and
 * return a lossless PNG Blob.  Modern evergreen browsers already
 * support PNG encoding through `HTMLCanvasElement.toBlob` as well as
 * the newer `OffscreenCanvas.convertToBlob`, so we only keep those two
 * code-paths.
 */
export async function cropToPng(image: File, crop: CropArea): Promise<Blob> {
    // 1️⃣  Decode into an ImageBitmap (fast, preserves EXIF)
    const bitmap = await createImageBitmap(image)

    // 2️⃣  Prepare a canvas of the cropped size – use OffscreenCanvas when
    //     available to avoid blocking the main thread; otherwise fall back
    //     to a normal <canvas> element.
    const useOffscreen = typeof OffscreenCanvas !== 'undefined'
    const canvas: OffscreenCanvas | HTMLCanvasElement = useOffscreen
        ? new OffscreenCanvas(crop.width, crop.height)
        : document.createElement('canvas')

    if (!useOffscreen) {
        canvas.width = crop.width
        canvas.height = crop.height
    }

    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null
    if (!ctx) throw new Error('Failed to obtain 2d context')

    // 3️⃣  Draw the crop region from the original bitmap onto (0,0)
    ctx.drawImage(
        bitmap,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        crop.width,
        crop.height,
    )

    // 4️⃣  Encode as lossless Png.
    if (useOffscreen) {
        return (canvas as OffscreenCanvas).convertToBlob({ type: 'image/png' })
    }

    return new Promise<Blob>((resolve, reject) => {
        (canvas as HTMLCanvasElement).toBlob((blob) => {
            blob ? resolve(blob) : reject(new Error('Failed to create PNG blob'))
        }, 'image/png')
    })
}
